/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { api } from "jiffy-api";
import { track } from "jiffy-analytics";
import { notifyError } from "common/utils/app-monitoring/notify";
import { shouldReportError } from "common/utils/request/request";
import { nextPollDelay } from "common/utils/polling-backoff";

const MAX_CONCURRENT_POLLS = 2;

type PollingInstance = {
  isPolling: boolean;
  pollCount: number;
  attempt: number;
  lastStatus?: number | undefined;
  nextDelayMs?: number | undefined;
  startTime: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
  catalogNumber: string | null;
  dtfChannelUuid: string | null;
  checkArtworkExists: ((artworkId: number) => boolean) | null;
  // MN-4782: invoked from #handlePollingTimeout so the store can flip
  // enhancementTimedOut[artworkId] + fire user-visible telemetry.
  onTimeout: ((artworkId: number) => void) | null;
};

type PollingOptions = {
  catalogNumber?: string | null;
  dtfChannelUuid?: string | null;
  checkArtworkExists?: ((artworkId: number) => boolean) | null;
  onTimeout?: ((artworkId: number) => void) | null;
  initialDelay?: number;
};

type ArtworkHistoryItem = Record<string, unknown> & {
  id: number | string;
  current_enhancement_state?: string;
  currentEnhancementState?: string;
  ordered?: Record<string, unknown>;
  original?: Record<string, unknown>;
};

type GetArtworkHistoryWithOptions = (
  params: {
    artwork_ids: number[];
    catalog_number: string | null;
    dtf_channel_uuid: string | null;
  },
  options?: { silent?: boolean; signal?: AbortSignal }
) => Promise<{ artworks: ArtworkHistoryItem[] }>;

// Transfers polling service
class TransfersPollingService {
  INITIAL_DELAY: number;

  MAX_POLLING_DURATION: number;

  POLLING_INTERVAL: number;

  pollingInstances: Map<number, PollingInstance>;

  private inFlight: Set<number>;

  constructor() {
    this.pollingInstances = new Map();
    this.inFlight = new Set();
    this.INITIAL_DELAY = 40000;
    this.POLLING_INTERVAL = 10000;
    this.MAX_POLLING_DURATION = 180000;
  }

  startPolling(
    artworkId: number | string,
    onEnhancementComplete: (artwork: Record<string, unknown>) => void,
    options: PollingOptions = {}
  ) {
    artworkId = Number(artworkId);
    if (this.pollingInstances.has(artworkId)) return;
    const {
      catalogNumber = null,
      dtfChannelUuid = null,
      checkArtworkExists = null,
      onTimeout = null,
      initialDelay = this.INITIAL_DELAY
    } = options;
    const instance: PollingInstance = {
      isPolling: true,
      pollCount: 0,
      attempt: 0,
      startTime: Date.now(),
      timeoutId: null,
      catalogNumber,
      dtfChannelUuid,
      checkArtworkExists,
      onTimeout
    };
    this.pollingInstances.set(artworkId, instance);
    instance.timeoutId = setTimeout(() => {
      if (instance.checkArtworkExists && !instance.checkArtworkExists(artworkId as number)) {
        this.stopPolling(artworkId as number);
        return;
      }
      track("fallback_polling_started", {
        artwork_id: String(artworkId),
        catalog_number: instance.catalogNumber,
        page: "transfers_pdp"
      });
      this.#pollArtwork(artworkId as number, onEnhancementComplete);
    }, initialDelay);
  }

  async #pollArtwork(
    artworkId: number,
    onEnhancementComplete: (artwork: Record<string, unknown>) => void
  ) {
    const instance = this.pollingInstances.get(artworkId);
    if (!instance || !instance.isPolling) return;

    if (instance.checkArtworkExists && !instance.checkArtworkExists(artworkId)) {
      track("fallback_polling_stopped_artwork_removed", {
        artwork_id: String(artworkId),
        catalog_number: instance.catalogNumber,
        page: "transfers_pdp",
        poll_count: instance.pollCount
      });
      this.stopPolling(artworkId);
      return;
    }
    instance.pollCount += 1;
    const shouldContinue = await this.#checkArtworkStatus(artworkId, onEnhancementComplete);
    if (!shouldContinue) return;
    if (this.#isPollingTimedOut(instance)) {
      this.#handlePollingTimeout(artworkId);
      return;
    }
    this.#scheduleNextPoll(artworkId, onEnhancementComplete);
  }

  async #checkArtworkStatus(
    artworkId: number,
    onEnhancementComplete: (artwork: Record<string, unknown>) => void
  ) {
    const instance = this.pollingInstances.get(artworkId);
    if (!instance) return false;
    if (this.inFlight.size >= MAX_CONCURRENT_POLLS && !this.inFlight.has(artworkId)) {
      if (this.#isPollingTimedOut(instance)) {
        this.#handlePollingTimeout(artworkId);
        return false;
      }
      // Best-effort fairness: each artwork keeps its own scheduled timer.
      instance.nextDelayMs = this.POLLING_INTERVAL;
      this.#scheduleNextPoll(artworkId, onEnhancementComplete);
      return false;
    }

    this.inFlight.add(artworkId);
    const controller = new AbortController();
    // Per-request timeout (9s = 90% of POLLING_INTERVAL) — independent of
    // the inter-request backoff in `instance.nextDelayMs`. A slow API
    // doesn't get faster on retry, so the timeout stays fixed while the
    // interval between requests scales.
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, this.POLLING_INTERVAL * 0.9);

    try {
      const artworkHistoryParams = {
        artwork_ids: [artworkId],
        catalog_number: instance.catalogNumber,
        dtf_channel_uuid: instance.dtfChannelUuid
      };
      const getArtworkHistory = api.dtf
        .getArtworkHistory as unknown as GetArtworkHistoryWithOptions;
      const response = await getArtworkHistory(artworkHistoryParams, {
        silent: true,
        signal: controller.signal
      });

      const artwork = response.artworks.find(a => Number(a.id) === artworkId);
      const enhancementState =
        artwork?.current_enhancement_state || artwork?.currentEnhancementState;
      instance.attempt = 0;
      instance.lastStatus = undefined;
      instance.nextDelayMs = undefined;
      if (enhancementState === "complete_processing") {
        this.#handleEnhancementComplete(artworkId, artwork, onEnhancementComplete);
        return false;
      }
    } catch (error) {
      const err = error as { status?: number; response?: unknown; message?: string; name?: string };
      const status = err?.status;
      if (status === 401) {
        track("fallback_polling_stopped_session_expired", {
          artwork_id: String(artworkId),
          catalog_number: instance.catalogNumber,
          page: "transfers_pdp"
        });
        this.stopPolling(artworkId);
        return false;
      }
      const result = nextPollDelay({
        error: err,
        attempt: instance.attempt,
        lastStatus: instance.lastStatus,
        baseIntervalMs: this.POLLING_INTERVAL,
        capMs: 45000,
        remainingBudgetMs: this.MAX_POLLING_DURATION - (Date.now() - instance.startTime)
      });

      if (result.shouldStop) {
        this.stopPolling(artworkId);
        return false;
      }

      instance.attempt = result.attemptNext;
      instance.lastStatus = result.lastStatusNext;
      instance.nextDelayMs = result.delayMs;

      if (!shouldReportError(error) || (status && status >= 400 && status < 500)) return true;

      notifyError("Transfers polling error:", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          hook: "transfers_polling_service",
          step: "check_artwork_status",
          artwork_id: String(artworkId),
          status_code: status
        }
      });

      if (status && status >= 500) {
        track("fallback_polling_stopped_server_error", {
          artwork_id: String(artworkId),
          catalog_number: instance.catalogNumber,
          page: "transfers_pdp"
        });
        this.stopPolling(artworkId);
        return false;
      }
    } finally {
      clearTimeout(timeoutId);
      this.inFlight.delete(artworkId);
    }
    return true;
  }

  #handleEnhancementComplete(
    artworkId: number,
    artwork: ArtworkHistoryItem | undefined,
    onEnhancementComplete: (artwork: Record<string, unknown>) => void
  ) {
    const instance = this.pollingInstances.get(artworkId);
    const catalogNumber = instance?.catalogNumber;

    try {
      track("fallback_polling_enhancement_completed", {
        artwork_id: String(artworkId),
        catalog_number: catalogNumber,
        page: "transfers_pdp"
      });
      if (artwork) {
        const enhancedArtwork = TransfersPollingService.#transformArtworkData(artwork);
        onEnhancementComplete(enhancedArtwork);
      }
      this.stopPolling(artworkId);
    } catch (error) {
      notifyError("Error in handleEnhancementComplete:", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          hook: "transfers_polling_service",
          step: "handle_enhancement_complete",
          artwork_id: String(artworkId)
        }
      });
      this.stopPolling(artworkId);
    }
  }

  #isPollingTimedOut(instance: PollingInstance) {
    return Date.now() - instance.startTime >= this.MAX_POLLING_DURATION;
  }

  #handlePollingTimeout(artworkId: number) {
    const instance = this.pollingInstances.get(artworkId);
    // MN-4782: notify the caller BEFORE stopPolling so the store can flip
    // enhancementTimedOut[artworkId] + fire the user-visible telemetry event
    // and block Add-to-Cart. Without this callback the user previously saw a
    // forever-spinner with no feedback after the 3-min MAX_POLLING_DURATION.
    try {
      instance?.onTimeout?.(artworkId);
    } catch (err) {
      // Record to AppMonitoring — not just console — per exception-handling
      // rules. Rails.logger / console-only drops the signal silently and a
      // regression here would recreate the exact forever-spinner class of
      // bug this PR was built to fix. Never let a consumer callback throw
      // abort polling teardown.
      notifyError(err instanceof Error ? err : new Error(String(err)), {
        params: {
          artwork_id: String(artworkId),
          source: "transfers_polling_service.on_timeout",
          catalog_number: instance?.catalogNumber
        }
      });
    }
    this.stopPolling(artworkId);
    track("fallback_polling_stopped", {
      artwork_id: String(artworkId),
      catalog_number: instance?.catalogNumber,
      page: "transfers_pdp"
    });
  }

  #scheduleNextPoll(
    artworkId: number,
    onEnhancementComplete: (artwork: Record<string, unknown>) => void
  ) {
    const instance = this.pollingInstances.get(artworkId);
    if (instance) {
      instance.timeoutId = setTimeout(() => {
        this.#pollArtwork(artworkId, onEnhancementComplete);
      }, instance.nextDelayMs ?? this.POLLING_INTERVAL);
    }
  }

  stopPolling(artworkId: number) {
    const instance = this.pollingInstances.get(artworkId);
    if (instance) {
      if (instance.timeoutId) clearTimeout(instance.timeoutId);
      instance.attempt = 0;
      instance.lastStatus = undefined;
      instance.nextDelayMs = undefined;
      this.inFlight.delete(artworkId);
      this.pollingInstances.delete(artworkId);
    }
  }

  stopAllPolling() {
    this.pollingInstances.forEach((_instance: PollingInstance, artworkId: number) => {
      this.stopPolling(artworkId);
    });
  }

  isPolling(artworkId: number) {
    return this.pollingInstances.has(artworkId);
  }

  static #transformArtworkData(artwork: ArtworkHistoryItem) {
    const { ordered, original } = artwork;
    const imageUrl = ordered?.["image_url"] || ordered?.["imageUrl"];
    return {
      id: artwork.id,
      noBgUrl: ordered?.["no_bg_url"] || ordered?.["noBgUrl"],
      noBgNoTrimUrl: ordered?.["no_bg_no_trim_url"] || ordered?.["noBgNoTrimUrl"],
      withBgUrl: ordered?.["with_bg_url"] || ordered?.["withBgUrl"],
      imageUrl,
      url: imageUrl,
      originalImageUrl: original?.["image_url"] || original?.["imageUrl"],
      canRemoveBg: ordered?.["can_remove_bg"] ?? ordered?.["canRemoveBg"],
      removeBackground: ordered?.["remove_background"] ?? ordered?.["removeBackground"],
      metadata: ordered?.["metadata"],
      enhancedMetadata: ordered?.["enhanced_metadata"] || ordered?.["enhancedMetadata"],
      originalMetadata: ordered?.["original_metadata"] || ordered?.["originalMetadata"],
      withBgMetadata: ordered?.["with_bg_metadata"] || ordered?.["withBgMetadata"]
    };
  }
}

export default new TransfersPollingService();
